import { computed } from 'vue'

import { useTransitDataQuery } from '@/composables/useTransitDataQuery'
import type { BusRoute, DirectionKey, Stop, TransitDataset } from '@/types'
import { createStopIndex } from '@/utils/stopIndex'
import type { StopIndex } from '@/utils/stopIndex'
import { normalizeForSearch } from '@/utils/textSearch'

/**
 * One occurrence of a route at a stop.
 *
 * A stop served by both directions of one route yields TWO entries with the same
 * `route.code` and different `direction` — which is exactly what the Passing
 * route tab renders, one card per direction with its own Departer/Return arrow.
 */
export interface RouteAtStop {
  route: BusRoute
  direction: DirectionKey
  /** 0-based position along that direction's stop list. */
  stopIndex: number
  totalStops: number
}

/** A stop with its search key precomputed. */
export interface IndexedStop extends Stop {
  normalizedName: string
}

export interface TransitIndex {
  stopsById: Map<number, IndexedStop>
  routesByStopId: Map<number, RouteAtStop[]>
  routesByCode: Map<string, BusRoute>
  /** Deduped by id, for search and for the map. */
  allStops: IndexedStop[]
  /** Grid index over `allStops`, for viewport and radius queries. */
  spatial: StopIndex
}

/**
 * `routes.json` is route-major; three of the four screens need stop-major access.
 *
 * Built in a single pass over the 12,625 stop-visits. Pure and exported so the
 * indexing can be tested against real data without mounting anything.
 */
export function buildTransitIndex(dataset: TransitDataset): TransitIndex {
  const stopsById = new Map<number, IndexedStop>()
  const routesByStopId = new Map<number, RouteAtStop[]>()
  const routesByCode = new Map<string, BusRoute>()

  for (const route of dataset.routes) {
    routesByCode.set(route.code, route)

    for (const direction of ['outbound', 'inbound'] as const) {
      const stops = route.directions[direction].stops

      stops.forEach((stop, stopIndex) => {
        // Stop names are NOT unique (3,288 distinct names across 4,991 ids), so
        // identity is always the id. Normalising here rather than per keystroke
        // is the whole reason this map exists.
        if (!stopsById.has(stop.id)) {
          stopsById.set(stop.id, { ...stop, normalizedName: normalizeForSearch(stop.name) })
        }

        const entry: RouteAtStop = { route, direction, stopIndex, totalStops: stops.length }
        const existing = routesByStopId.get(stop.id)
        if (existing) existing.push(entry)
        else routesByStopId.set(stop.id, [entry])
      })
    }
  }

  const allStops = [...stopsById.values()]

  return {
    stopsById,
    routesByStopId,
    routesByCode,
    allStops,
    spatial: createStopIndex(allStops),
  }
}

/**
 * Memoised on the dataset's object identity rather than on a loaded flag: a
 * refetch produces a new object and rebuilds, while an unrelated re-render
 * reuses the existing index. `useTransitDataQuery` caches forever, so in
 * practice this builds once per session.
 */
export function useTransitIndex() {
  const { data, isPending, error } = useTransitDataQuery()

  const index = computed<TransitIndex | undefined>(() =>
    data.value ? buildTransitIndex(data.value) : undefined,
  )

  return { index, isPending, error }
}

/** Distinct route codes serving a stop, in first-seen order. */
export function routeCodesAtStop(routes: RouteAtStop[]): string[] {
  const seen = new Set<string>()
  for (const entry of routes) seen.add(entry.route.code)
  return [...seen]
}
